import { useEffect, useState, type FormEvent } from 'react';
import {
  fetchInstitutionInvite,
  submitInstitutionOnboarding,
  type InstitutionInviteInfo,
} from '../lib/api';

/**
 * Public, token-gated institution-onboarding wizard. A wellness-office
 * contact lands here via the link the founder emailed them. They fill
 * the setup form once; the resulting community waits in
 * status='pending' for the founder to approve it before sign-ups open.
 *
 * No auth — the recipient doesn't have an account yet. The token is
 * the trust handshake. On submit, the form 410s if the token was
 * already used so a leaked link can't stand up a duplicate community.
 *
 * Visually keyed to the marketing landing's warm-correspondence
 * aesthetic (italic Fraunces title, paper card, gold fleuron) so a
 * wellness officer who got the email feels they're in the same place
 * the link promised.
 */

type Step = 'invite' | 'form' | 'sent' | 'expired' | 'notfound';

export function InstitutionOnboarding() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') ?? '';

  const [step, setStep] = useState<Step>('invite');
  const [invite, setInvite] = useState<InstitutionInviteInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sentMessage, setSentMessage] = useState('');

  // Form state — populated from the invite where possible.
  const [institutionName, setInstitutionName] = useState('');
  const [institutionType, setInstitutionType] = useState<'university' | 'employer' | 'health_plan'>(
    'university',
  );
  const [contactName, setContactName] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [emailDomainsText, setEmailDomainsText] = useState('');
  const [crisisName, setCrisisName] = useState('');
  const [crisisContact, setCrisisContact] = useState('');
  const [crisisType, setCrisisType] = useState<
    'university_counseling' | 'employer_eap' | 'health_plan_intake' | 'community_provider'
  >('university_counseling');
  const [crisisResourceNote, setCrisisResourceNote] = useState('');
  const [crisisRunbook, setCrisisRunbook] = useState('');
  const [moderatorEmailsText, setModeratorEmailsText] = useState('');

  useEffect(() => {
    if (!token) {
      setStep('notfound');
      return;
    }
    fetchInstitutionInvite(token).then((res) => {
      if (res.ok) {
        setInvite(res.data);
        setInstitutionName(res.data.institutionName);
        setStep('form');
      } else {
        setStep(
          res.error.includes('expired') || res.error.includes('used') ? 'expired' : 'notfound',
        );
      }
    });
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;

    const domains = emailDomainsText
      .split(/[\s,]+/)
      .map((d) => d.trim().toLowerCase().replace(/^@/, ''))
      .filter((d) => d.length > 0);
    if (domains.length === 0) {
      setError('Add at least one email domain (e.g. yourschool.edu).');
      return;
    }
    const moderatorEmails = moderatorEmailsText
      .split(/[\s,]+/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
    if (moderatorEmails.length === 0) {
      setError('Add at least one moderator email — these are the staff who handle the queue.');
      return;
    }
    if (!crisisName.trim() || !crisisContact.trim()) {
      setError(
        'Crisis escalation endpoint is required. This is who LessAlone routes high-severity flags to at your institution.',
      );
      return;
    }

    setSubmitting(true);
    setError(null);
    const result = await submitInstitutionOnboarding(token, {
      institutionName: institutionName.trim(),
      institutionType,
      contactName: contactName.trim(),
      contactRole: contactRole.trim() || undefined,
      emailDomains: domains,
      crisisEscalationName: crisisName.trim(),
      crisisEscalationContact: crisisContact.trim(),
      crisisEscalationType: crisisType,
      crisisResourceNote: crisisResourceNote.trim() || undefined,
      crisisRunbook: crisisRunbook.trim() || undefined,
      moderatorEmails,
    });
    setSubmitting(false);
    if (result.ok) {
      setSentMessage(result.message);
      setStep('sent');
    } else {
      setError(result.error);
    }
  }

  return (
    <main className="institution-onboard">
      <header className="io-header">
        <span className="wordmark">
          <em className="wordmark-accent">Less</em>Alone
        </span>
        <span className="io-eyebrow">Institution setup</span>
      </header>

      {step === 'invite' && (
        <div className="io-card">
          <p className="muted">Loading your invite…</p>
        </div>
      )}

      {step === 'notfound' && (
        <div className="io-card">
          <h1>This link isn't valid.</h1>
          <p className="muted">
            The token in the URL doesn't match any invite. If you got this link in an email and
            think it should still work, reply to that email — a LessAlone founder will look into it.
          </p>
        </div>
      )}

      {step === 'expired' && (
        <div className="io-card">
          <h1>This link has already been used or has expired.</h1>
          <p className="muted">
            Each invite is one-shot and expires after 14 days. Reply to the original invite email
            and a founder will issue a new one.
          </p>
        </div>
      )}

      {step === 'sent' && (
        <div className="io-card io-card--sent">
          <div className="io-fleuron" aria-hidden="true">
            ✦
          </div>
          <h1>Submitted.</h1>
          <p>{sentMessage}</p>
          <p className="muted">
            You can close this tab. A founder will email you and your moderators once your community
            is approved.
          </p>
        </div>
      )}

      {step === 'form' && invite && (
        <form onSubmit={onSubmit} className="io-card io-form">
          <div className="io-fleuron" aria-hidden="true">
            ✦
          </div>
          <h1>
            Welcome, <em>{invite.institutionName}</em>.
          </h1>
          <p className="io-lede">
            Four short sections. The most important one is your crisis escalation endpoint — that's
            who LessAlone routes a self-harm flag to at your institution. We can't open sign-ups
            without it.
          </p>
          {invite.note && (
            <p className="io-note">
              <strong>Note from the founder:</strong> {invite.note}
            </p>
          )}

          <section className="io-section">
            <h2>1 · About your institution</h2>
            <label className="io-field">
              <span>Institution name</span>
              <input
                type="text"
                value={institutionName}
                onChange={(e) => setInstitutionName(e.target.value)}
                required
                maxLength={120}
              />
            </label>
            <label className="io-field">
              <span>Institution type</span>
              <select
                value={institutionType}
                onChange={(e) =>
                  setInstitutionType(e.target.value as 'university' | 'employer' | 'health_plan')
                }
              >
                <option value="university">University / college</option>
                <option value="employer">Employer</option>
                <option value="health_plan">Health plan</option>
              </select>
            </label>
            <label className="io-field">
              <span>Your name</span>
              <input
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                required
                maxLength={120}
                placeholder="e.g. Maya Robinson"
              />
            </label>
            <label className="io-field">
              <span>Your role (optional)</span>
              <input
                type="text"
                value={contactRole}
                onChange={(e) => setContactRole(e.target.value)}
                maxLength={120}
                placeholder="e.g. Director, Counseling & Wellness"
              />
            </label>
          </section>

          <section className="io-section">
            <h2>2 · Email domains you own</h2>
            <p className="muted">
              Comma- or newline-separated. Anyone with an email at one of these domains will be able
              to sign up. Usually one or two: <code>yourschool.edu</code>,{' '}
              <code>students.yourschool.edu</code>.
            </p>
            <label className="io-field">
              <textarea
                value={emailDomainsText}
                onChange={(e) => setEmailDomainsText(e.target.value)}
                rows={3}
                placeholder="yourschool.edu, students.yourschool.edu"
              />
            </label>
          </section>

          <section className="io-section io-section--required">
            <h2>3 · Crisis escalation endpoint</h2>
            <p className="muted">
              If a student sends a self-harm signal, the moderator's run-book points them here.
              Required.
            </p>
            <label className="io-field">
              <span>Endpoint name</span>
              <input
                type="text"
                value={crisisName}
                onChange={(e) => setCrisisName(e.target.value)}
                required
                maxLength={120}
                placeholder="e.g. UNC Counseling & Wellness Services on-call"
              />
            </label>
            <label className="io-field">
              <span>Contact (phone, email, or URL)</span>
              <input
                type="text"
                value={crisisContact}
                onChange={(e) => setCrisisContact(e.target.value)}
                required
                maxLength={254}
                placeholder="e.g. 919-555-0100, or counseling@yourschool.edu"
              />
            </label>
            <label className="io-field">
              <span>Endpoint type</span>
              <select
                value={crisisType}
                onChange={(e) =>
                  setCrisisType(
                    e.target.value as
                      | 'university_counseling'
                      | 'employer_eap'
                      | 'health_plan_intake'
                      | 'community_provider',
                  )
                }
              >
                <option value="university_counseling">University counseling center</option>
                <option value="employer_eap">Employee assistance program</option>
                <option value="health_plan_intake">Health plan intake line</option>
                <option value="community_provider">Community provider</option>
              </select>
            </label>
            <label className="io-field">
              <span>Public-facing resource note (optional, shown to students in crisis)</span>
              <textarea
                value={crisisResourceNote}
                onChange={(e) => setCrisisResourceNote(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder="e.g. 'Walk-ins welcome 8a–6p, M–F. After hours, call 988 or the public safety line.'"
              />
            </label>
            <label className="io-field">
              <span>Moderator run-book (optional, shown to moderators on a self-harm flag)</span>
              <textarea
                value={crisisRunbook}
                onChange={(e) => setCrisisRunbook(e.target.value)}
                rows={4}
                maxLength={10_000}
                placeholder="e.g. step-by-step instructions for the moderator: who to call, how to document, escalation thresholds."
              />
            </label>
          </section>

          <section className="io-section">
            <h2>4 · Moderators</h2>
            <p className="muted">
              The wellness-office staff who will see flagged messages and act on them. One email per
              line. You'll be added automatically; these are your team.
            </p>
            <label className="io-field">
              <textarea
                value={moderatorEmailsText}
                onChange={(e) => setModeratorEmailsText(e.target.value)}
                rows={4}
                placeholder={'mod1@yourschool.edu\nmod2@yourschool.edu'}
              />
            </label>
          </section>

          {error && <p className="error">{error}</p>}
          <div className="io-actions">
            <button type="submit" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit for review'}
            </button>
          </div>
          <p className="muted io-fineprint">
            By submitting, you confirm you represent {invite.institutionName} and are authorised to
            stand up a LessAlone community on its behalf.
          </p>
        </form>
      )}
    </main>
  );
}
